import { useState, useCallback } from "react";

type FormValues = Record<string, any>;

export interface UseFormValues<T extends FormValues> {
  values: T;
  setFieldValue: <K extends keyof T>(field: K, value: T[K]) => void;
  setFormValues: (newValues: Partial<T>) => void;
  resetForm: () => void;
}

export function useForm<T extends FormValues>(
  initialValues: T
): UseFormValues<T> {
  const [values, setValues] = useState<T>(initialValues);

  // Change single property
  const setFieldValue = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      setValues((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  // Change multiple properties
  const setFormValues = useCallback((newValues: Partial<T>) => {
    setValues((prev) => ({
      ...prev,
      ...newValues,
    }));
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setValues(initialValues);
  }, [initialValues]);

  return {
    values,
    setFieldValue,
    setFormValues,
    resetForm,
  };
}
